import React, { useContext, useRef, useState } from 'react';
import {
    Callout,
    DirectionalHint,
    FontIcon,
    IconButton,
    Link,
    Stack,
    Text,
} from '@fluentui/react';
import { useId } from '@uifabric/react-hooks';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../../../Common/api/ABHub/ABHubGlobalConfig';
import { CopyableValue } from '../../../../Common/components/CopyableValue';
import { Guid } from '../../../../Common/models/Guid';
import { SRM_SUPPORT_URL } from '../../../../Common/sovereignClouds/sovereignCloudConfig';
import { getCloud } from '../../../../Common/sovereignClouds/sovereignCloudData';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';

interface ReleaseCloudHelpProps {
    cloudName?: string;
    releaseCorrelationId?: Guid;
    releaseId?: number;
    organizationName?: string;
    projectName?: string;
}

const CloudAccessPopover: React.FC<
    ReleaseCloudHelpProps & {
        cloudName: string;
        isPublicPortal: boolean;
    }
> = ({
    cloudName,
    releaseId,
    organizationName,
    projectName,
    isPublicPortal,
}) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const anchor = useRef<HTMLSpanElement>(null);
    const [open, setOpen] = useState(false);
    const calloutId = useId('cloud-access');
    const titleId = `${calloutId}-title`;
    const cloud = getCloud(cloudName);
    const displayName = cloud?.displayName ?? cloudName;
    const searchId =
        releaseId !== undefined && releaseId > 0
            ? releaseId.toString()
            : undefined;
    const releaseContext = [organizationName, projectName]
        .map((value) => value?.trim())
        .filter(Boolean)
        .join(' / ');

    return (
        <span
            ref={anchor}
            style={{
                display: 'inline-flex',
                alignSelf: 'center',
                flexShrink: 0,
            }}
        >
            <Link
                as="button"
                type="button"
                aria-label={`Cloud info for ${displayName}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls={open ? calloutId : undefined}
                onClick={() => setOpen(!open)}
                styles={{
                    root: {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        minHeight: 28,
                    },
                }}
            >
                <FontIcon iconName="Info" aria-hidden="true" />
                Cloud info
            </Link>
            {open && (
                <Callout
                    id={calloutId}
                    role="dialog"
                    ariaLabelledBy={titleId}
                    target={anchor.current}
                    onDismiss={() => setOpen(false)}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    gapSpace={6}
                    setInitialFocus
                    shouldRestoreFocus
                    styles={{
                        root: { width: 344, maxWidth: 'calc(100vw - 24px)' },
                        calloutMain: {
                            padding: 16,
                            maxHeight: 'calc(100vh - 32px)',
                            overflowY: 'auto',
                            backgroundColor: theme.surfaces.container,
                            color: theme.semanticColors.bodyText,
                        },
                    }}
                >
                    <Stack tokens={{ childrenGap: 12 }}>
                        <Stack
                            horizontal
                            verticalAlign="center"
                            horizontalAlign="space-between"
                            tokens={{ childrenGap: 8 }}
                        >
                            <Text
                                as="h2"
                                id={titleId}
                                variant="mediumPlus"
                                styles={{
                                    root: { margin: 0, fontWeight: 600 },
                                }}
                            >
                                About {displayName}
                            </Text>
                            <IconButton
                                iconProps={{ iconName: 'Cancel' }}
                                title="Close"
                                ariaLabel={`Close About ${displayName}`}
                                onClick={() => setOpen(false)}
                            />
                        </Stack>
                        {cloud && (
                            <>
                                <Text
                                    variant="medium"
                                    styles={{ root: { lineHeight: '20px' } }}
                                >
                                    <strong>Account:</strong>{' '}
                                    {cloud.accountFormat}
                                </Text>
                                <Text
                                    variant="medium"
                                    styles={{ root: { lineHeight: '20px' } }}
                                >
                                    <strong>Access:</strong>{' '}
                                    {cloud.accessRequirements.join(' \u00b7 ')}
                                </Text>
                            </>
                        )}
                        {isPublicPortal ? (
                            cloud?.portalDeployed === false ? (
                                <Text
                                    variant="medium"
                                    styles={{ root: { lineHeight: '20px' } }}
                                >
                                    The SRM portal is not yet available in{' '}
                                    {displayName}. Contact the SRM team in that
                                    cloud for help.
                                </Text>
                            ) : (
                                <>
                                    {searchId && (
                                        <CopyableValue
                                            label="Release ID"
                                            value={searchId}
                                        />
                                    )}
                                    <Text
                                        variant="medium"
                                        styles={{
                                            root: {
                                                lineHeight: '20px',
                                                overflowWrap: 'anywhere',
                                            },
                                        }}
                                    >
                                        Open the SRM portal inside {displayName}
                                        . Go to <strong>Releases</strong>,{' '}
                                        {searchId ? (
                                            <>
                                                search for{' '}
                                                <strong>{searchId}</strong>
                                            </>
                                        ) : (
                                            'search by release name'
                                        )}
                                        , and select{' '}
                                        {releaseContext ? (
                                            <>
                                                the release matching{' '}
                                                <strong>
                                                    {releaseContext}
                                                </strong>
                                            </>
                                        ) : (
                                            'the matching release'
                                        )}
                                        .
                                    </Text>
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: { lineHeight: '20px' },
                                        }}
                                    >
                                        If it does not appear, check the filters
                                        and date range.
                                    </Text>
                                </>
                            )
                        ) : (
                            <Text
                                variant="medium"
                                styles={{ root: { lineHeight: '20px' } }}
                            >
                                You are viewing the {displayName} portal.
                                Processing logs are available in build and
                                deployment details when you have access.
                            </Text>
                        )}
                        {isPublicPortal && cloud && SRM_SUPPORT_URL && (
                            <Link
                                href={SRM_SUPPORT_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                styles={{
                                    root: {
                                        color: theme.isInverted
                                            ? theme.semanticColors.bodyText
                                            : theme.semanticColors.link,
                                    },
                                }}
                            >
                                Cloud access documentation
                            </Link>
                        )}
                    </Stack>
                </Callout>
            )}
        </span>
    );
};

export const ReleaseCloudHelp: React.FC<ReleaseCloudHelpProps> = ({
    cloudName,
    releaseCorrelationId,
    releaseId,
    organizationName,
    projectName,
}) => {
    const hostingCloud = getConfig(GlobalConfigProperties.Cloud)?.trim();
    const destination = cloudName?.trim();
    if (
        !hostingCloud ||
        !destination ||
        destination.toUpperCase() === 'PUBLIC'
    ) {
        return null;
    }

    const isPublicPortal = hostingCloud.toUpperCase() === 'PUBLIC';
    if (
        !isPublicPortal &&
        hostingCloud.toUpperCase() !== destination.toUpperCase()
    ) {
        return null;
    }

    return (
        <CloudAccessPopover
            key={`${hostingCloud}:${destination}:${releaseCorrelationId}:${releaseId}:${organizationName}:${projectName}`}
            cloudName={destination}
            releaseId={releaseId}
            organizationName={organizationName}
            projectName={projectName}
            isPublicPortal={isPublicPortal}
        />
    );
};
